import { useRef, useState } from 'react';
import { useI18n } from '@react/i18n';
import { useFormContext } from 'react-hook-form';
import { CheckoutApi } from '../api/checkoutApi';
import { UseVatMachine } from '../hooks/useVatMachine';
import { CheckoutFormValues, CheckoutState, IC_PATTERN, normalizeIc } from '../types';
import Field from './Field';
import { Collapse, Spinner, flashInputs } from './ui';

interface Props {
    api: CheckoutApi;
    state: CheckoutState;
    vat: UseVatMachine;
    /** pole zamcena po autofillu z registru (ARES/VIES) */
    lockedFields: string[];
    setLockedFields: (updater: (prev: string[]) => string[]) => void;
}

/**
 * Firemní údaje podle trhu:
 * - cz: IČO + „Doplnit dle ARES", DIČ jen informativní (DPH se účtuje vždy)
 * - sk: IČO lookup (Premier/registry) + IČ DPH ověřené přes VIES → reverse-charge
 * - international: VAT ID ověřené přes VIES → reverse-charge
 */
export default function CompanySection({ api, state, vat, lockedFields, setLockedFields }: Props) {
    const t = useI18n();
    const { register, setValue, getValues, watch } = useFormContext<CheckoutFormValues>();
    const companyOpen = watch('company_open');
    const market = state.market;
    const locked = state.customer?.isPartnerLocked ?? false;

    const [icExists, setIcExists] = useState(false);
    const [lookupRunning, setLookupRunning] = useState(false);
    const [lookupError, setLookupError] = useState<string | null>(null);
    const [groupVatWarning, setGroupVatWarning] = useState(false);
    const icCheckTimer = useRef<number | null>(null);
    const icDirty = useRef(false);

    const showIcField = market !== 'international';
    const usesVies = market !== 'cz';

    const dicLabel = market === 'cz' ? t('DIČ') : market === 'sk' ? t('IČ DPH (napr.: SK0123456789)') : t('VAT ID (for example: FR0123456789)');

    // ARES je jen CZ zdroj — SK/int hledají v jiných registrech
    const lookupFailedMsg = market === 'cz'
        ? t('Nepovedlo se nalézt IČ v Aresu')
        : t('Nepodařilo se najít firmu podle IČ');

    const onIcInput = (ic: string) => {
        icDirty.current = true;
        setLockedFields(() => []);
        setLookupError(null);
        if (icCheckTimer.current !== null) {
            window.clearTimeout(icCheckTimer.current);
        }
        if (ic.length !== 8) {
            setIcExists(false);
            return;
        }
        icCheckTimer.current = window.setTimeout(async () => {
            try {
                const result = await api.icExistCheck(ic);
                setIcExists(result.exists);
            } catch {
                // nekritické
            }
        }, 500);
    };

    const runLookup = async () => {
        if (lookupRunning) {
            return;
        }
        const raw = getValues('company.ic');
        const ic = normalizeIc(raw);
        if (ic !== raw) {
            setValue('company.ic', ic, { shouldValidate: true });
        }
        if (!IC_PATTERN.test(ic)) {
            // bez hlášky by klik na tlačítko vypadal jako nefunkční; zámky z VIES autofillu nechat
            setLookupError(t('IČ musí být 8 čísel'));
            return;
        }

        setLookupRunning(true);
        setLookupError(null);
        setGroupVatWarning(false);
        try {
            const result = await api.companyLookup(ic);
            if (!result.found || !result.company) {
                setLockedFields(() => []);
                setLookupError(lookupFailedMsg);
                return;
            }

            const c = result.company;
            const filled: string[] = [];
            if (c.companyName) {
                filled.push('frm-company-name');
                setValue('company.name', c.companyName, { shouldValidate: true });
            }
            if (c.dic) {
                filled.push('frm-company-dic');
                setValue('company.dic', c.dic, { shouldValidate: true });
                // nalezené DIČ hned ověřit: SK/int přes VIES (rozhoduje o DPH),
                // CZ informativně (server si uloží stav → souhrn zvýrazní bez DPH)
                vat.onDicChanged(c.dic, true);
            }
            if (c.street) {
                filled.push('frm-billing-street');
                setValue('billing.street', c.street, { shouldValidate: true });
            }
            if (c.city) {
                filled.push('frm-billing-city');
                setValue('billing.city', c.city, { shouldValidate: true });
            }
            if (c.zip) {
                filled.push('frm-billing-zip');
                setValue('billing.zip', c.zip, { shouldValidate: true });
            }
            flashInputs(filled);
            // fakturacni udaje firmy musi odpovidat registru -> zamknout
            const paths = filled.map((id) => id.replace('frm-', '').replace('-', '.'));
            setLockedFields((prev) => Array.from(new Set([...prev, ...paths])));
            setGroupVatWarning(result.groupVatWarning);
        } catch {
            setLockedFields(() => []);
            setLookupError(lookupFailedMsg);
        } finally {
            setLookupRunning(false);
        }
    };

    const vatState = vat.state;

    // Mezinárodní trh nemá IČ lookup — explicitní tlačítko spustí VIES validaci
    // hned a autofill doplní název firmy + adresu (jako legacy); renderuje se
    // v pravém sloupci vedle pole (help slot Fieldu)
    const viesButton = market === 'international' && !locked ? (
        <div className="form-group">
            <button
                type="button"
                onClick={() => vat.onDicChanged(getValues('company.dic'), true, true)}
                disabled={vatState.kind === 'validating' || vatState.kind === 'rateLimited'}
                className="btn btn--custom-1 btn--inp btn--block rc-vies-btn"
            >
                {vatState.kind === 'validating' && <Spinner className="rc-btn-spinner" />}
                <span>{vatState.kind === 'validating' ? t('Validating VAT ID...') : t('Fill in the data via VIES')}</span>
            </button>
        </div>
    ) : undefined;

    const dicField = (
        <>
            {vatState.kind === 'validPayer' && vatState.countryMismatch && (
                <div className="alert alert-warning margin-bottom">
                    <p>{t('Validating via VIES failed, please try again in a minute')}</p>
                </div>
            )}

            <Field
                name="company.dic"
                label={dicLabel}
                help={market === 'cz' ? <p>{t('Nevyplňujte, pokud nejste plátci DPH.')}</p> : viesButton}
            >
                <input
                    id="frm-company-dic"
                    className="form-control"
                    readOnly={locked || lockedFields.includes('company.dic')}
                    {...register('company.dic', {
                        onChange: (e) => vat.onDicChanged(e.target.value),
                        onBlur: (e) => vat.onDicChanged(e.target.value, true),
                    })}
                />
            </Field>

            {usesVies && (
                <VatStatusBox vat={vat} />
            )}
        </>
    );

    return (
        <fieldset id="checkout-company-section">
            <div className="b-company clearfix">
                <label className={`b-check b-check--large${companyOpen ? ' active-panel' : ''}`}>
                    <span>{t('Firemní údaje')}</span>
                    <span>{t('(Když nakupujete na firmu)')}</span>
                    <input type="checkbox" {...register('company_open')} />
                </label>

                <Collapse open={companyOpen}>
                    <div className="b-company__content in">
                        {showIcField && (
                            <>
                                {icExists && <p className="alert alert-warning">{t('Toto IČ je u nás již registrováno, pro správné zobrazení vašich cen se prosím přihlašte')}</p>}
                                <div className="clearfix">
                                    <div className="form-group col-xm-6 col-md-5 no-padding--left">
                                        <label className="control-label" htmlFor="frm-company-ic">{t('IČ')} <span>*</span></label>
                                        <input
                                            id="frm-company-ic"
                                            className="form-control"
                                            readOnly={locked}
                                            {...register('company.ic', {
                                                validate: (value, values) =>
                                                    !values.company_open || IC_PATTERN.test(normalizeIc(value)) || t('IČ musí být 8 čísel'),
                                                onChange: (e) => onIcInput(normalizeIc(e.target.value)),
                                                // autolookup pri opusteni pole se zmenenym kompletnim ICO (chovani stareho webu)
                                                onBlur: () => {
                                                    const raw = getValues('company.ic');
                                                    const ic = normalizeIc(raw);
                                                    if (ic !== raw) {
                                                        setValue('company.ic', ic, { shouldValidate: true });
                                                    }
                                                    if (!icDirty.current) {
                                                        return;
                                                    }
                                                    icDirty.current = false;
                                                    if (IC_PATTERN.test(ic)) {
                                                        void runLookup();
                                                    }
                                                },
                                            })}
                                        />
                                    </div>
                                    {!locked && (
                                        <div className="col-xm-6">
                                            <div className="form-group">
                                                <button
                                                    type="button"
                                                    onClick={() => void runLookup()}
                                                    disabled={lookupRunning}
                                                    className="btn btn--custom-1 btn--inp btn--block btn--bottom"
                                                >
                                                    {lookupRunning && <Spinner className="rc-btn-spinner" />}
                                                    <span>{lookupRunning
                                                        ? t('Loading company data...')
                                                        : market === 'cz' ? t('Doplnit dle Ares') : market === 'sk' ? t('Doplniť z registra') : t('Fill in the data via VIES')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {lookupError && <p className="msg msg--error">{lookupError}</p>}
                                {groupVatWarning && <p className="alert alert-warning">{t('Group registration – This VAT ID corresponds to a Group')}</p>}
                            </>
                        )}

                        {/* Mezinárodní trh: z DIČ se přes VIES dotahuje název firmy → pole je první */}
                        {market === 'international' && dicField}

                        <Field name="company.name" label={t('Firma')} required>
                            <input
                                id="frm-company-name"
                                className="form-control"
                                readOnly={locked || lockedFields.includes('company.name')}
                                {...register('company.name', {
                                    validate: (value, values) => !values.company_open || value.trim() !== '' || t('Zadejte prosím název firmy'),
                                })}
                            />
                        </Field>

                        {market !== 'international' && dicField}

                        <Field name="company.referee" label={t('Interní číslo objednávky')}>
                            <input
                                id="frm-company-referee"
                                className="form-control"
                                maxLength={32}
                                {...register('company.referee')}
                            />
                        </Field>
                    </div>
                </Collapse>
            </div>
        </fieldset>
    );
}

/** Stav VIES validace: spinner / výsledek / ruční volba plátce při výpadku. */
function VatStatusBox({ vat }: { vat: UseVatMachine }) {
    const t = useI18n();
    const s = vat.state;
    const [choosing, setChoosing] = useState(false);

    const choose = async (payer: boolean) => {
        setChoosing(true);
        try {
            await vat.chooseManual(payer);
        } finally {
            setChoosing(false);
        }
    };

    return (
        <div className="clearfix">
            {s.kind === 'validating' && (
                <div className="rc-vat rc-vat--loading" key="loading">
                    <Spinner />
                    <div>{t('Validating VAT ID...')} <strong>{s.dic}</strong></div>
                </div>
            )}

            {(s.kind === 'rateLimited' || s.kind === 'failed') && (
                <div className="rc-vat rc-vat--warn" key="retry">
                    {s.kind === 'rateLimited' ? <Spinner /> : <span className="rc-vat__icon" aria-hidden="true">!</span>}
                    <div>{t('Validating via VIES failed, please try again in a minute')}</div>
                </div>
            )}

            {s.kind === 'validPayer' && (
                <div className="rc-vat rc-vat--valid" key="valid">
                    <span className="rc-vat__icon" aria-hidden="true">✓</span>
                    <div>{s.message ?? t('You are a VAT payer. Due to reverse-charge, we will not charge VAT.')}</div>
                </div>
            )}

            {s.kind === 'invalid' && (
                <div className="rc-vat rc-vat--invalid" key="invalid">
                    <span className="rc-vat__icon" aria-hidden="true">✕</span>
                    <div>{s.message ?? t('Your entered VAT ID is invalid.')}</div>
                </div>
            )}

            {(s.kind === 'unavailable' || s.kind === 'manualChoice') && (
                <div className={`rc-vat ${s.kind === 'manualChoice' && s.payer ? 'rc-vat--valid' : 'rc-vat--warn'}`} id="fn-vies-radio-div" key="choice">
                    <span className="rc-vat__icon" aria-hidden="true">{s.kind === 'manualChoice' ? (s.payer ? '✓' : '✕') : '!'}</span>
                    <div>
                        {s.kind === 'unavailable' && (
                            <p>{s.message ?? t('Your entered VAT ID could not be verified. Seems VIES os offline. Please, choose below if you are a VAT payer.')}</p>
                        )}
                        <p><strong>{t('I am a VAT payer (will be charged without VAT)')}</strong></p>
                        <div>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    name="viesRadio"
                                    disabled={choosing}
                                    checked={s.kind === 'manualChoice' && s.payer}
                                    onChange={() => void choose(true)}
                                />{' '}
                                {t('Yes')}
                            </label>
                            <label className="radio-inline">
                                <input
                                    type="radio"
                                    name="viesRadio"
                                    disabled={choosing}
                                    checked={s.kind === 'manualChoice' && !s.payer}
                                    onChange={() => void choose(false)}
                                />{' '}
                                {t('No')}
                            </label>
                            {choosing && <Spinner className="rc-btn-spinner" />}
                        </div>
                        {s.kind === 'unavailable' && <p>{t('Please choose the correct VAT payer status')}</p>}
                    </div>
                </div>
            )}
        </div>
    );
}
